import { useIsReadOnly } from '@/lib/casl'
import { useFieldContext } from '@/lib/form-contexts'
import { cn } from '@/lib/utils'
import type { ComponentProps, KeyboardEvent, ReactNode } from 'react'
import { Tooltip, TooltipContent, TooltipTrigger, type TooltipContentVariant } from '../ui/tooltip'
import { Button, type ButtonProps, type ButtonVariant } from '../ui/button'
import { Label } from '../ui/label'
import {
  SegmentedControl,
  SegmentedControlItem,
  type SegmentedControlOrientation,
} from '../ui/segmented-control'
import FieldInfo from './FieldInfo'

export type RadioButtonGroupClassNames = {
  container?: string
  button?: string
  label?: string
  selectedButton?: string
  unselectedButton?: string
}

export type RadioButtonGroupOptionClassNames = {
  button?: string
  label?: string
  selectedButton?: string
  unselectedButton?: string
}

export type RadioButtonGroupAppearance = 'default' | 'segmented' | 'selectorCard'

const radioButtonGroupAppearances: Record<RadioButtonGroupAppearance, RadioButtonGroupClassNames> =
  {
    default: {},
    segmented: {
      button:
        'h-auto min-h-10 min-w-0 items-center px-4 py-2.5 text-left whitespace-normal has-[>svg]:pt-0',
      label: 'min-w-0 whitespace-normal',
      unselectedButton:
        'bg-background text-foreground hover:bg-muted dark:bg-card dark:hover:bg-muted',
    },
    selectorCard: {
      button:
        'h-auto min-w-0 justify-start gap-3 rounded-xl border px-4 py-3 text-left whitespace-normal shadow-none transition-none',
      label: 'min-w-0 text-base font-semibold leading-tight whitespace-normal',
      selectedButton: 'z-10',
      unselectedButton:
        'border-border/70 bg-background text-foreground hover:border-border hover:bg-muted/45 hover:text-foreground dark:hover:border-white/20 dark:hover:bg-white/[0.06]',
    },
  }

export type RadioButtonGroupSelectedTone =
  | 'amethyst'
  | 'periwinkle'
  | 'mint'
  | 'success'
  | 'warning'
  | 'destructive'

const semanticToneClassNames: Record<
  RadioButtonGroupSelectedTone,
  { selected: string; unselected: string }
> = {
  amethyst: {
    selected:
      'bg-accentSecondary text-accentSecondary-foreground ring-selection-border hover:bg-accentSecondary hover:text-accentSecondary-foreground active:bg-accentSecondary active:text-accentSecondary-foreground',
    unselected:
      'hover:bg-accentSecondary hover:text-accentSecondary-foreground hover:ring-1 hover:ring-inset hover:ring-selection-border active:bg-accentSecondary active:text-accentSecondary-foreground active:ring-1 active:ring-inset active:ring-selection-border',
  },
  periwinkle: {
    selected:
      'bg-accent text-accent-foreground ring-status-info-border hover:bg-accent hover:text-accent-foreground active:bg-accent active:text-accent-foreground',
    unselected:
      'hover:bg-accent hover:text-accent-foreground hover:ring-1 hover:ring-inset hover:ring-status-info-border active:bg-accent active:text-accent-foreground active:ring-1 active:ring-inset active:ring-status-info-border',
  },
  mint: {
    selected:
      'bg-brand-mint text-status-success-foreground ring-status-success-border hover:bg-brand-mint hover:text-status-success-foreground active:bg-brand-mint active:text-status-success-foreground',
    unselected:
      'hover:bg-brand-mint hover:text-status-success-foreground hover:ring-1 hover:ring-inset hover:ring-status-success-border active:bg-brand-mint active:text-status-success-foreground active:ring-1 active:ring-inset active:ring-status-success-border',
  },
  success: {
    selected:
      'bg-status-success-background text-status-success-foreground ring-status-success-border hover:bg-status-success-background hover:text-status-success-foreground active:bg-status-success-background active:text-status-success-foreground focus-visible:outline-status-success-foreground',
    unselected:
      'hover:bg-status-success-background hover:text-status-success-foreground hover:ring-1 hover:ring-inset hover:ring-status-success-border active:bg-status-success-background active:text-status-success-foreground active:ring-1 active:ring-inset active:ring-status-success-border focus-visible:outline-status-success-foreground dark:hover:bg-status-success-background dark:hover:text-status-success-foreground',
  },
  warning: {
    selected:
      'bg-status-warning-background text-status-warning-foreground ring-status-warning-border hover:bg-status-warning-background hover:text-status-warning-foreground active:bg-status-warning-background active:text-status-warning-foreground focus-visible:outline-status-warning-foreground',
    unselected:
      'hover:bg-status-warning-background hover:text-status-warning-foreground hover:ring-1 hover:ring-inset hover:ring-status-warning-border active:bg-status-warning-background active:text-status-warning-foreground active:ring-1 active:ring-inset active:ring-status-warning-border focus-visible:outline-status-warning-foreground dark:hover:bg-status-warning-background dark:hover:text-status-warning-foreground',
  },
  destructive: {
    selected:
      'bg-status-destructive-background text-status-destructive-foreground ring-status-destructive-border hover:bg-status-destructive-background hover:text-status-destructive-foreground active:bg-status-destructive-background active:text-status-destructive-foreground focus-visible:outline-status-destructive-foreground',
    unselected:
      'hover:bg-status-destructive-background hover:text-status-destructive-foreground hover:ring-1 hover:ring-inset hover:ring-status-destructive-border active:bg-status-destructive-background active:text-status-destructive-foreground active:ring-1 active:ring-inset active:ring-status-destructive-border focus-visible:outline-status-destructive-foreground dark:hover:bg-status-destructive-background dark:hover:text-status-destructive-foreground',
  },
}

export type RadioButtonGroupOptionId = string | number | boolean
export type RadioButtonGroupValue = string | number | boolean | null | undefined

export type RadioButtonGroupOption = {
  id: RadioButtonGroupOptionId
  name: string
  ariaLabel?: string
  selectedTone?: RadioButtonGroupSelectedTone
  start?: ReactNode
  end?: ReactNode
  tooltip?: ReactNode
  tooltipVariant?: TooltipContentVariant
  tooltipClassName?: string
}

type RadioButtonGroupOwnProps = {
  appearance?: RadioButtonGroupAppearance
  id?: string
  label?: string | null
  options?: readonly RadioButtonGroupOption[]
  labelEnd?: ReactNode
  className?: string
  classNames?: RadioButtonGroupClassNames
  disabled?: boolean
  displayValue?: RadioButtonGroupValue
  joined?: boolean
  optionClassNames?: Record<string, RadioButtonGroupOptionClassNames>
  onValueChange?: (nextValue: RadioButtonGroupValue, previousValue: RadioButtonGroupValue) => void
  orientation?: SegmentedControlOrientation
  parseValue?: (value: RadioButtonGroupOptionId) => RadioButtonGroupValue
  selectedVariant?: ButtonVariant
  size?: ButtonProps['size']
  unselectedVariant?: ButtonVariant
}

export type RadioButtonGroupProps = RadioButtonGroupOwnProps &
  Omit<ComponentProps<'div'>, keyof RadioButtonGroupOwnProps>

export default function RadioButtonGroup({
  appearance = 'default',
  id,
  label = null,
  options = [],
  labelEnd = null,
  className = '',
  classNames = {},
  disabled = false,
  displayValue,
  joined = true,
  optionClassNames = {},
  onValueChange,
  orientation = 'horizontal',
  parseValue = (value) => String(value),
  selectedVariant = 'selection',
  size = 'default',
  unselectedVariant = 'outline',
  ...props
}: RadioButtonGroupProps) {
  const field = useFieldContext<RadioButtonGroupValue>()
  const { isReadOnly } = useIsReadOnly()
  const appearanceClassNames = radioButtonGroupAppearances[appearance] ?? {}
  const normalizedClassNames = {
    container: cn(appearanceClassNames.container, classNames.container),
    button: cn(appearanceClassNames.button, classNames.button),
    label: cn(appearanceClassNames.label, classNames.label),
    selectedButton: cn(appearanceClassNames.selectedButton, classNames.selectedButton),
    unselectedButton: cn(appearanceClassNames.unselectedButton, classNames.unselectedButton),
  }

  // Validate options count
  if (options.length > 4) {
    throw new Error('RadioButtonGroup supports a maximum of 4 options')
  }

  if (options.length === 0) {
    return null
  }

  const isDisabled = isReadOnly || disabled
  const currentValue = String(displayValue ?? field?.state?.value ?? '')
  const Group = joined ? SegmentedControl : 'div'
  const OptionButton = joined ? SegmentedControlItem : Button

  const commitOption = (optionId: RadioButtonGroupOptionId) => {
    const previousValue = field?.state?.value
    const nextValue = parseValue(optionId)
    field.handleChange(nextValue)
    onValueChange?.(nextValue, previousValue)
  }

  const handleOptionClick = (optionId: RadioButtonGroupOptionId) => {
    if (isDisabled) return
    commitOption(optionId)
  }

  // Every index below is within `options`, which is non-empty past the early return above.
  const commitAndFocusOption = (target: EventTarget, index: number) => {
    const option = options[index]
    if (!option) return
    commitOption(option.id)
    const button =
      target instanceof Node
        ? target.parentElement?.querySelector(`[data-option-id="${String(option.id)}"]`)
        : undefined
    if (button instanceof HTMLElement) button.focus()
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (isDisabled) return

    const currentIndex = options.findIndex((opt) => String(opt.id) === currentValue)

    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault()
      const prevIndex = currentIndex > 0 ? currentIndex - 1 : options.length - 1
      // Focus the previous button
      commitAndFocusOption(e.target, prevIndex)
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault()
      const nextIndex = currentIndex < options.length - 1 ? currentIndex + 1 : 0
      // Focus the next button
      commitAndFocusOption(e.target, nextIndex)
    } else if (e.key === 'Home') {
      e.preventDefault()
      commitAndFocusOption(e.target, 0)
    } else if (e.key === 'End') {
      e.preventDefault()
      const lastIndex = options.length - 1
      commitAndFocusOption(e.target, lastIndex)
    }
  }

  return (
    <div
      className={cn(
        'flex flex-col gap-y-2',
        joined && orientation === 'responsive' && '@container/segmented-control',
        className
      )}
    >
      {label && (
        <div className="inline-flex items-center">
          <Label className="" htmlFor={id || field.name}>
            {label}
          </Label>
          {labelEnd}
        </div>
      )}

      <Group
        role="radiogroup"
        aria-label={label || field.name}
        aria-orientation={orientation === 'responsive' ? undefined : orientation}
        className={cn(!joined && 'inline-flex', normalizedClassNames.container)}
        {...(joined ? { orientation } : {})}
        {...props}
      >
        {options.map((option) => {
          const optionId = String(option.id)
          const optionClasses = optionClassNames[optionId] ?? {}
          const isSelected = currentValue === optionId
          const toneClasses = option.selectedTone
            ? semanticToneClassNames[option.selectedTone]
            : undefined
          const button = (
            <OptionButton
              key={String(option.id)}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={option.ariaLabel ?? option.name}
              data-option-id={option.id}
              data-selected-tone={option.selectedTone}
              {...(joined
                ? {
                    selected: isSelected,
                    variant: unselectedVariant,
                    selectedVariant,
                    orientation,
                  }
                : { variant: isSelected ? selectedVariant : unselectedVariant })}
              id={id ? `${id}-${option.id}` : undefined}
              size={size}
              disabled={isDisabled}
              className={cn(
                !joined && 'focus-visible:z-10',
                normalizedClassNames.button,
                optionClasses.button,
                isSelected
                  ? normalizedClassNames.selectedButton
                  : normalizedClassNames.unselectedButton,
                toneClasses?.[isSelected ? 'selected' : 'unselected'],
                isSelected ? optionClasses.selectedButton : optionClasses.unselectedButton
              )}
              onClick={() => handleOptionClick(option.id)}
              onKeyDown={handleKeyDown}
              onBlur={field.handleBlur}
            >
              {/* Keep icon starts wrapped in inline-flex spans. Direct SVG children trigger Button's SVG-specific spacing rules and make segmented controls look off-center. */}
              {option.start}
              <span
                className={cn(
                  'inline-flex items-center leading-none',
                  normalizedClassNames.label,
                  optionClasses.label
                )}
              >
                {option.name}
              </span>
              {option.end}
            </OptionButton>
          )

          if (option.tooltip) {
            return (
              <Tooltip key={String(option.id)}>
                <TooltipTrigger asChild>{button}</TooltipTrigger>
                <TooltipContent
                  variant={option.tooltipVariant || 'periwinkle'}
                  className={option.tooltipClassName}
                >
                  {option.tooltip}
                </TooltipContent>
              </Tooltip>
            )
          }

          return button
        })}
      </Group>

      <FieldInfo />
    </div>
  )
}
